import { Box, ListItem, ListItemIcon, ListItemText, Skeleton } from '@mui/material';
import { YOUTUBE_BROADCAST_THUMBNAIL_WIDTH } from '../shared/consts';
import { YoutubeBroadcastItemSize } from './YoutubeBroadcastItem';

interface YoutubeBroadcastItemSkeletonProps {
  /** Vertical: list row. Horizontal: status screen panorama strip. Both show the accent ribbon. */
  layout?: 'horizontal' | 'vertical';
  /** Visual density; mirrors YoutubeBroadcastItem title and metadata visibility. */
  size?: YoutubeBroadcastItemSize;
  /** Fixed item height for horizontal layout; also sizes the thumbnail to 16:9. */
  height?: number;
}

/** Skeleton placeholder matching YoutubeBroadcastItem layout while broadcasts load. */
export function YoutubeBroadcastItemSkeleton({
  layout = 'vertical',
  size = 'large',
  height,
}: YoutubeBroadcastItemSkeletonProps) {
  const isHorizontal = layout === 'horizontal';
  const thumbWidth = isHorizontal ? undefined : YOUTUBE_BROADCAST_THUMBNAIL_WIDTH;
  const showTitle = size !== 'xsmall';
  const showEndTimeAndDuration = size !== 'xsmall' && size !== 'small';

  const thumbnailSkeleton = (
    <Skeleton
      variant="rounded"
      width={thumbWidth}
      sx={{
        borderRadius: 1,
        flexShrink: 0,
        ...(isHorizontal
          ? { alignSelf: 'stretch', width: 'auto', aspectRatio: '16/9' }
          : { aspectRatio: '16/9' }),
      }}
    />
  );

  return (
    <ListItem
      disablePadding
      sx={{
        mb: isHorizontal ? 0 : 1,
        ...(isHorizontal && { width: '100%', minWidth: 0 }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          px: 1,
          py: 1,
          gap: 1,
          alignItems: 'center',
          ...(isHorizontal && height != null && { height }),
        }}
      >
        <Box
          sx={{
            width: 3,
            alignSelf: 'stretch',
            bgcolor: 'action.hover',
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
        {isHorizontal ? (
          thumbnailSkeleton
        ) : (
          <ListItemIcon sx={{ minWidth: YOUTUBE_BROADCAST_THUMBNAIL_WIDTH, mr: 1.5 }}>
            {thumbnailSkeleton}
          </ListItemIcon>
        )}
        <ListItemText
          sx={{ my: 0 }}
          primary={
            showTitle ? (
              <Skeleton width="85%" height={size === 'small' ? 18 : 22} sx={{ mb: 0.5 }} />
            ) : undefined
          }
          secondary={
            <Box component="div" sx={{ mt: showTitle ? 0.75 : 0 }}>
              <Skeleton width="70%" height={14} />
              {showEndTimeAndDuration && <Skeleton width="55%" height={14} sx={{ mt: 0.5 }} />}
              <Skeleton
                variant="rounded"
                width={56}
                height={20}
                sx={{ mt: size === 'xsmall' ? 0.5 : 1, borderRadius: 10 }}
              />
            </Box>
          }
          slotProps={{
            secondary: { component: 'div' },
          }}
        />
      </Box>
    </ListItem>
  );
}
